import {
  BAN_DEFAULT_SECONDS,
  describeDuration,
  type ActionSummary,
  type ActionTarget,
  type CommandStatus,
  type SkipReason,
} from '@bartizo/shared';

/**
 * How an action and its commands read on screen (knowledge doc section 12).
 *
 * Written once, because the list, the detail page and the dialog must say the same
 * thing about the same state — "stopped" in one place and "cancelled" in another
 * would read as two different outcomes.
 */

/** Section 12's example durations, 4 h first because it is the default. */
export const BAN_DURATIONS: readonly { seconds: number; label: string }[] = [
  { seconds: 3_600, label: '1 hour' },
  { seconds: BAN_DEFAULT_SECONDS, label: '4 hours' },
  { seconds: 86_400, label: '1 day' },
  { seconds: 7 * 86_400, label: '7 days' },
  { seconds: 30 * 86_400, label: '30 days (the maximum)' },
];

const text = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/** "Ban 198.51.100.7 for 4h" — from the arguments as the API accepted them. */
export function describeAction(action: Pick<ActionSummary, 'type' | 'args'>): string {
  if (action.type === 'upgrade_crowdsec_hub') return 'Upgrade CrowdSec’s hub items';
  if (action.type === 'apply_compliance_fixes') {
    const fixes = action.args['fixes'];
    const count = Array.isArray(fixes) ? fixes.length : 0;
    return count === 1 ? 'Apply 1 compliance fix' : `Apply ${String(count)} compliance fixes`;
  }
  const ip = text(action.args['ip']) ?? 'an address';
  if (action.type === 'unban_ip') return `Unban ${ip}`;
  const seconds = action.args['duration_seconds'];
  const verb = action.args['remediation'] === 'captcha' ? 'Challenge' : 'Ban';
  return typeof seconds === 'number'
    ? `${verb} ${ip} for ${describeDuration(seconds)}`
    : `${verb} ${ip}`;
}

/** The reason as the person typed it, not CrowdSec's `bartizo: <email>:` form. */
export const reasonOf = (action: Pick<ActionSummary, 'args'>): string | undefined =>
  text(action.args['reason']);

export function describeTarget(target: ActionTarget, groupName?: string): string {
  switch (target.kind) {
    case 'all':
      return 'every server';
    case 'server':
      return target.id;
    case 'group':
      return `the group ${groupName ?? target.id}`;
  }
}

export const STATUS_LABEL: Readonly<Record<CommandStatus, string>> = {
  pending: 'waiting for the server',
  sent: 'running',
  done: 'done',
  failed: 'failed',
  rejected: 'refused',
  expired: 'expired',
  cancelled: 'stopped',
  skipped: 'skipped',
};

export const STATUS_TONE: Readonly<Record<CommandStatus, string>> = {
  pending: 'badge-accent',
  sent: 'badge-accent',
  done: 'badge-success',
  failed: 'badge-danger',
  rejected: 'badge-danger',
  expired: 'badge-warning',
  cancelled: 'badge-muted',
  skipped: 'badge-muted',
};

/** The order a summary lists them in: what needs looking at first. */
export const STATUS_ORDER: readonly CommandStatus[] = [
  'failed',
  'rejected',
  'expired',
  'pending',
  'sent',
  'done',
  'cancelled',
  'skipped',
];

export const isOpen = (status: CommandStatus): boolean => status === 'pending' || status === 'sent';

export const SKIP_LABEL: Readonly<Record<SkipReason, string>> = {
  revoked: 'removed from the fleet',
  pending: 'still awaiting approval',
  no_crowdsec: 'CrowdSec is not installed there',
  proxy_address: 'the address is a proxy in front of it',
  no_challenge: 'its web layer cannot show a challenge',
};
